import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { AgGridReact } from 'ag-grid-react';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import Icon, { IconSizes, IconVariants } from '../../../components/foundations/Icon';
import { parseCSVToGrid } from '../../../utils/csvUtils';
import { MAX_DISPLAY_ROWS } from '../../constants';
import ArtifactLoader from './ArtifactLoader';
import css from './GridArtifact.css';

const DEFAULT_COL_DEF = {
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 50,
};

const MAX_GRID_HEIGHT = 450;

const GridArtifactInner = ({ data, metadata }) => {
    const wrapperRef = useRef(null);
    const columnMetadata = metadata?.column_metadata;

    const { rowData, columnDefs, truncationInfo } = useMemo(() => {
        if (!data || typeof data !== 'string') {
            return { rowData: [], columnDefs: [], truncationInfo: null };
        }

        const lines = data.split('\n');
        const totalRows = Math.max(0, lines.length - 1);
        let csvToRender = data;
        let truncInfo = null;

        if (totalRows > MAX_DISPLAY_ROWS) {
            csvToRender = lines.slice(0, MAX_DISPLAY_ROWS + 1).join('\n');
            truncInfo = {
                is_truncated: true,
                truncation_message: `Showing first ${MAX_DISPLAY_ROWS} of ${totalRows} rows for performance`,
                display_row_count: MAX_DISPLAY_ROWS,
                row_count: totalRows,
            };
        }

        const parseOptions = columnMetadata ? { columnMetadata } : undefined;
        return { ...parseCSVToGrid(csvToRender, parseOptions), truncationInfo: truncInfo };
    }, [data, columnMetadata]);

    const displayMetadata = truncationInfo || metadata;

    if (!rowData || rowData.length === 0) {
        return (
            <div className={css.emptyState}>
                <Icon name="table" size={IconSizes.LARGE} />
                <div>No data available</div>
            </div>
        );
    }

    const handleFirstDataRendered = ({ api }) => {
        setTimeout(() => {
            api.autoSizeAllColumns();
            const wrapper = wrapperRef.current;
            if (!wrapper) return;

            const totalWidth = api.getAllDisplayedColumns().reduce((sum, col) => sum + col.getActualWidth(), 0);

            if (totalWidth > 0) {
                const maxWidth = wrapper.parentElement?.clientWidth ?? totalWidth;
                wrapper.style.width = `${Math.min(totalWidth, maxWidth)}px`;
            }

            const headerEl = wrapper.querySelector('.ag-header');
            const headerH = headerEl ? headerEl.getBoundingClientRect().height : 49;
            const rowCount = api.getDisplayedRowCount();
            const naturalH = headerH + rowCount * 42;
            wrapper.style.height = `${Math.min(naturalH, MAX_GRID_HEIGHT)}px`;
        }, 0);
    };

    return (
        <div className={css.gridArtifact}>
            <div ref={wrapperRef} className={`${css.gridWrapper} ag-theme-quartz`}>
                <AgGridReact
                    modules={[AllEnterpriseModule]}
                    columnDefs={columnDefs}
                    rowData={rowData}
                    defaultColDef={DEFAULT_COL_DEF}
                    suppressColumnVirtualisation
                    onFirstDataRendered={handleFirstDataRendered}
                />
            </div>
            {displayMetadata?.is_truncated && (
                <div className={css.truncationWarning}>
                    <Icon name="info-circle" variant={IconVariants.WARNING} size={IconSizes.SMALL} />
                    <span>
                        {displayMetadata.truncation_message ||
                            `Showing ${displayMetadata.display_row_count} of ${displayMetadata.row_count} rows`}
                    </span>
                </div>
            )}
        </div>
    );
};

const GridArtifact = ({ artifact }) => (
    <ArtifactLoader artifact={artifact}>
        {data => <GridArtifactInner data={data} metadata={artifact.metadata} />}
    </ArtifactLoader>
);

GridArtifact.propTypes = {
    artifact: PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        data: PropTypes.string,
        content_url: PropTypes.string,
        metadata: PropTypes.shape({
            display_row_count: PropTypes.number,
            is_truncated: PropTypes.bool,
            row_count: PropTypes.number,
            truncation_message: PropTypes.string,
            columns: PropTypes.arrayOf(PropTypes.string),
        }),
    }).isRequired,
};

export default GridArtifact;
